import React from 'react';
import { Network, BadgeCheck, Timer, Users, Target } from 'lucide-react';

const AdvantagesSection = () => {
  const advantages = [
    {
      icon: Network,
      title: 'Global Supplier Network',
      description: 'Access to an extensive network of certified suppliers and manufacturers across all major aviation markets.'
    },
    {
      icon: BadgeCheck,
      title: 'Quality-Certified Components',
      description: 'All parts are verified for authenticity and compliance with international aviation standards.'
    },
    {
      icon: Timer,
      title: 'Fast Delivery and Logistics',
      description: 'Efficient procurement and shipping processes designed to minimize aircraft ground time.'
    },
    {
      icon: Users,
      title: 'Experienced Aviation Professionals',
      description: 'Our team brings decades of combined experience in aviation supply chain management.'
    },
    {
      icon: Target,
      title: 'Customer-Focused Service Approach',
      description: 'Dedicated account management and personalized solutions tailored to your specific requirements.'
    }
  ];

  return (
    <section id="quality" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#d4af37]/5 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12 sm:mb-16 space-y-3 sm:space-y-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#0a1628]">
            Our Competitive <span className="text-[#d4af37]">Advantages</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto px-4">
            What sets us apart in the aviation supply industry
          </p>
        </div>
        <div
          className="mb-16 sm:mb-20 max-w-4xl mx-auto px-4 py-8 bg-white/80 rounded-2xl shadow-lg border border-[#d4af37]/10 flex flex-col items-center gap-4 relative overflow-hidden"
          data-animate="fade-scale"
          data-testid="why-choose-description"
        >
          {/* Image Section */}
          <div className="w-full flex flex-col sm:flex-row justify-center items-center mb-6 gap-4 sm:gap-8 px-2">
            <div className="flex-1 flex justify-center">
              <img
                src="/ASA Membership.jpg"
                alt="Aviation Quality Assurance"
                className="w-full max-w-xs sm:max-w-md rounded-xl shadow-md border border-[#d4af37]/20 object-contain bg-[#f8fafc]"
                style={{ aspectRatio: '16/9' }}
              />
            </div>
            <div className="flex-1 flex justify-center">
              <img
                src="/iso.jpg"
                alt="ISO Certification"
                className="w-full max-w-xs sm:max-w-md rounded-xl shadow-md border border-[#d4af37]/20 object-contain bg-[#f8fafc]"
                style={{ aspectRatio: '16/9' }}
              />
            </div>
          </div>
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-20 h-20 bg-gradient-to-br from-[#d4af37]/30 to-[#fffbe6]/60 rounded-full blur-2xl opacity-60 pointer-events-none"></div>
          <div className="flex items-center gap-3 mb-2">
            <BadgeCheck className="w-7 h-7 text-[#d4af37] drop-shadow-md" />
            <span className="uppercase tracking-widest text-xs font-semibold text-[#d4af37]">Quality Commitment</span>
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-[#0a1628] text-center mb-2">Uncompromising Quality & Authenticity</h3>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed text-center">
            At <span className="font-semibold text-[#d4af37]">Turbowing Aviation</span>, quality is not just a goal—it's the foundation of everything we do. We understand that in aviation, there is no room for compromise when it comes to safety and reliability. Every component we supply undergoes rigorous verification to ensure it meets the highest industry standards.
          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed text-center">
            Our quality assurance processes are designed to provide complete confidence in the authenticity, airworthiness, and traceability of every part. From supplier selection to final delivery, we maintain strict controls and documentation at every step.
          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed text-center">
            We work exclusively with certified suppliers and manufacturers who share our commitment to excellence, ensuring that our customers receive only genuine, airworthy components backed by proper certification and documentation.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {advantages.map((advantage, index) => (
            <div 
              key={index}
              className="group relative p-6 sm:p-8 bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 hover:border-[#d4af37]/30"
            >
              {/* Icon */}
              <div className="mb-4 sm:mb-6">
                <div className="inline-flex p-3 sm:p-4 bg-gradient-to-br from-[#d4af37]/10 to-#d4af37/10 rounded-xl group-hover:from-[#d4af37] group-hover:to-#d4af37 transition-all duration-500">
                  <advantage.icon className="w-6 h-6 sm:w-8 sm:h-8 text-[#d4af37] group-hover:text-white transition-colors duration-500" />
                </div>
              </div>

              {/* Content */}
              <h3 className="text-lg sm:text-xl font-bold text-[#0a1628] mb-2 sm:mb-3 group-hover:text-[#d4af37] transition-colors duration-300">
                {advantage.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {advantage.description}
              </p>

              {/* Hover Border Effect */}
              <div className="absolute inset-0 rounded-2xl border-2 border-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdvantagesSection;
